'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Menu, X, Wallet, Bell, LogIn, BookOpen, PiggyBank, Gift, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

const navLinks = [
  { label: 'Packages', href: '/safari-packages-listing' },
  { label: 'Destinations', href: '/safari-packages-listing' },
  { label: 'About', href: '#' },
  { label: 'Contact', href: '#' },
];

export default function PublicTopbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const { user, signOut } = useAuth();
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const router = useRouter();

  useEffect(() => {
    if (!user) { setWalletBalance(null); return; }
    const supabase = createClient();
    supabase?.from('wallets')?.select('balance')?.eq('user_id', user?.id)?.eq('wallet_role', 'customer')?.single()?.then(({ data }) => {
        if (data) setWalletBalance(data?.balance);
      });
  }, [user]);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      setMobileOpen(false);
      router?.push('/sign-up-login-screen');
    } catch {
      setSigningOut(false);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 shadow-sm">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/safari-packages-listing" className="flex items-center gap-2">
            <span className="font-bold text-lg text-safari-green tracking-tight">
              Onthego <span className="text-safari-yellow">Safari</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks?.map((link) => (
              <Link
                key={`nav-${link?.label}`}
                href={link?.href}
                className="px-4 py-2 text-sm font-500 text-gray-600 hover:text-safari-green hover:bg-safari/5 rounded-lg transition-all duration-150"
              >
                {link?.label}
              </Link>
            ))}
            {user && (
              <Link
                href="/booking-history"
                className="px-4 py-2 text-sm font-500 text-gray-600 hover:text-safari-green hover:bg-safari/5 rounded-lg transition-all duration-150 flex items-center gap-1.5"
              >
                <BookOpen size={14} />
                My Bookings
              </Link>
            )}
            {user && (
              <Link
                href="/savings"
                className="px-4 py-2 text-sm font-500 text-gray-600 hover:text-safari-green hover:bg-safari/5 rounded-lg transition-all duration-150 flex items-center gap-1.5"
              >
                <PiggyBank size={14} />
                Savings
              </Link>
            )}
            {user && (
              <Link
                href="/referrals"
                className="px-4 py-2 text-sm font-500 text-gray-600 hover:text-safari-green hover:bg-safari/5 rounded-lg transition-all duration-150 flex items-center gap-1.5"
              >
                <Gift size={14} />
                Referrals
              </Link>
            )}
          </nav>

          {/* Right Actions */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <Link
                href="/wallet"
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-safari-green transition-colors"
              >
                <Wallet size={16} />
                <span>Wallet</span>
                <span className="bg-safari-yellow text-gray-900 text-xs font-bold px-1.5 py-0.5 rounded-md tabular-nums">
                  {walletBalance !== null ? `KES ${walletBalance?.toLocaleString()}` : 'KES —'}
                </span>
              </Link>
            ) : (
              <button className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-safari-green transition-colors">
                <Wallet size={16} />
                <span>Wallet</span>
              </button>
            )}
            <button className="relative p-2 text-gray-500 hover:text-safari-green hover:bg-gray-50 rounded-lg transition-all">
              <Bell size={18} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            {user ? (
              <div className="flex items-center gap-2">
                <Link
                  href="/booking-history"
                  className="flex items-center gap-2 px-4 py-2 bg-safari-green text-white text-sm font-semibold rounded-lg hover:bg-green-800 transition-all duration-150 active:scale-95"
                >
                  <BookOpen size={15} />
                  My Bookings
                </Link>
                <button
                  onClick={handleSignOut}
                  disabled={signingOut}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-150 disabled:opacity-50"
                  title="Sign out"
                >
                  {signingOut ? (
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                  ) : (
                    <LogOut size={16} />
                  )}
                  <span className="hidden lg:inline">Sign Out</span>
                </button>
              </div>
            ) : (
              <Link
                href="/sign-up-login-screen"
                className="flex items-center gap-2 px-4 py-2 bg-safari-green text-white text-sm font-semibold rounded-lg hover:bg-green-800 transition-all duration-150 active:scale-95"
              >
                <LogIn size={15} />
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden p-2 text-gray-600 hover:text-safari-green rounded-lg"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-1 fade-in">
          {navLinks?.map((link) => (
            <Link
              key={`mobile-nav-${link?.label}`}
              href={link?.href}
              className="block px-3 py-2.5 text-sm font-medium text-gray-700 hover:text-safari-green hover:bg-safari-lightGreen rounded-lg transition-all"
              onClick={() => setMobileOpen(false)}
            >
              {link?.label}
            </Link>
          ))}
          {user && (
            <>
              <Link
                href="/booking-history"
                className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 hover:text-safari-green hover:bg-safari-lightGreen rounded-lg transition-all"
                onClick={() => setMobileOpen(false)}
              >
                <BookOpen size={15} />
                My Bookings
              </Link>
              <Link
                href="/savings"
                className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 hover:text-safari-green hover:bg-safari-lightGreen rounded-lg transition-all"
                onClick={() => setMobileOpen(false)}
              >
                <PiggyBank size={15} />
                Savings
              </Link>
              <Link
                href="/referrals"
                className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 hover:text-safari-green hover:bg-safari-lightGreen rounded-lg transition-all"
                onClick={() => setMobileOpen(false)}
              >
                <Gift size={15} />
                Referrals & Rewards
              </Link>
              <Link
                href="/wallet"
                className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-700 hover:text-safari-green hover:bg-safari-lightGreen rounded-lg transition-all"
                onClick={() => setMobileOpen(false)}
              >
                <Wallet size={15} />
                My Wallet
                {walletBalance !== null && (
                  <span className="ml-auto bg-safari-yellow text-gray-900 text-xs font-bold px-1.5 py-0.5 rounded-md tabular-nums">
                    KES {walletBalance?.toLocaleString()}
                  </span>
                )}
              </Link>
            </>
          )}
          <div className="pt-3 border-t border-gray-100 flex flex-col gap-2">
            {user ? (
              <>
                <Link
                  href="/booking-history"
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-safari-green text-white text-sm font-semibold rounded-lg"
                  onClick={() => setMobileOpen(false)}
                >
                  <BookOpen size={15} />
                  My Bookings
                </Link>
                <button
                  onClick={handleSignOut}
                  disabled={signingOut}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 border border-red-200 text-red-600 text-sm font-semibold rounded-lg hover:bg-red-50 transition-all disabled:opacity-50"
                >
                  {signingOut ? (
                    <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
                  ) : (
                    <LogOut size={15} />
                  )}
                  Sign Out
                </button>
              </>
            ) : (
              <Link
                href="/sign-up-login-screen"
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-safari-green text-white text-sm font-semibold rounded-lg"
                onClick={() => setMobileOpen(false)}
              >
                <LogIn size={15} />
                Sign In
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}